// Generated by ExportFieldReferenceTests. Do not edit by hand.
//
// The paths, types and presence come from the export DTOs and the meanings from their
// XML doc comments, so this cannot drift from the file a dealer actually receives.
// Regenerate with EXPORT_GOLDEN_UPDATE=1 on the backend test run.

export interface ExportFieldReferenceEntry {
  /** Where the field sits in the file, with `/` for nesting. */
  path: string;
  /** What a reader needs to parse the value. */
  type: string;
  /** 'Always' or 'Optional'. */
  presence: string;
  /** What the field means, in business terms. */
  meaning: string;
}

export const exportSchemaVersion = '1.0';

export const quoteExportFields: ExportFieldReferenceEntry[] = [
  {
    path: 'schemaVersion',
    type: 'text',
    presence: 'Always',
    meaning: 'The contract version this file was written against.'
  },
  {
    path: 'documentType',
    type: 'text',
    presence: 'Always',
    meaning: '"quote" or "purchaseOrder". Lets one reader route both kinds of file.'
  },
  {
    path: 'generatedUtc',
    type: 'timestamp (ISO 8601 with offset)',
    presence: 'Always',
    meaning:
      'When the file was written, which is when the resource was issued. This is not the download time: the file is written once and never rewritten.'
  },
  {
    path: 'resourceId',
    type: 'identifier',
    presence: 'Always',
    meaning: 'The identifier of the resource this file was generated from.'
  },
  {
    path: 'documentNumber',
    type: 'text',
    presence: 'Optional',
    meaning: 'The human readable number of the document, as shown in the application.'
  },
  {
    path: 'currency',
    type: 'text',
    presence: 'Always',
    meaning:
      'The currency every monetary value in this file is expressed in. Money is published as a bare number; the currency is stated once, here.'
  },
  {
    path: 'quote',
    type: 'group',
    presence: 'Always',
    meaning: 'A group of related fields.'
  },
  {
    path: 'quote/number',
    type: 'text',
    presence: 'Optional',
    meaning: 'The quote number as shown in the application, for example Q-1042.'
  },
  {
    path: 'quote/customNumber',
    type: 'text',
    presence: 'Optional',
    meaning: "The dealer's own reference for this quote, where one was entered."
  },
  {
    path: 'quote/title',
    type: 'text',
    presence: 'Optional',
    meaning: 'The quote title.'
  },
  {
    path: 'quote/description',
    type: 'text',
    presence: 'Optional',
    meaning: 'The quote description.'
  },
  {
    path: 'quote/quoteType',
    type: 'text',
    presence: 'Optional',
    meaning: 'The kind of quote, as the Information page labels it.'
  },
  {
    path: 'quote/budget',
    type: 'number',
    presence: 'Optional',
    meaning: "The client's budget for the work, where one was recorded."
  },
  {
    path: 'quote/createdDate',
    type: 'timestamp (ISO 8601 with offset)',
    presence: 'Optional',
    meaning: 'When the quote was first created.'
  },
  {
    path: 'quote/lastModifiedDate',
    type: 'timestamp (ISO 8601 with offset)',
    presence: 'Optional',
    meaning: 'When the quote was last changed before it was issued.'
  },
  {
    path: 'quote/issuedDate',
    type: 'date (yyyy-MM-dd)',
    presence: 'Optional',
    meaning: 'The date the quote was issued. This is the date the document became final.'
  },
  {
    path: 'quote/validUntilDate',
    type: 'date (yyyy-MM-dd)',
    presence: 'Optional',
    meaning: 'The date the quote is valid until, where the quote carries one.'
  },
  {
    path: 'quote/validityPeriod',
    type: 'text',
    presence: 'Optional',
    meaning:
      'The validity as the page words it when it shows a period rather than a date, for example "30 days". Present only when that is what the user set.'
  },
  {
    path: 'quote/proposedDeliveryDate',
    type: 'date (yyyy-MM-dd)',
    presence: 'Optional',
    meaning: 'The proposed delivery or installation date.'
  },
  {
    path: 'quote/client',
    type: 'group',
    presence: 'Optional',
    meaning: 'A group of related fields.'
  },
  {
    path: 'quote/client/id',
    type: 'identifier',
    presence: 'Optional',
    meaning: "The client's identifier in this system, for matching against your own record."
  },
  {
    path: 'quote/client/name',
    type: 'text',
    presence: 'Optional',
    meaning: "The client's name, as it appears on the quote."
  },
  {
    path: 'quote/client/clientType',
    type: 'text',
    presence: 'Optional',
    meaning:
      'The client type or payment profile selected for this quote, for example a trade account on 30 day terms. The name of the profile, not a code.'
  },
  {
    path: 'quote/contact',
    type: 'group',
    presence: 'Optional',
    meaning: 'A group of related fields.'
  },
  {
    path: 'quote/contact/id',
    type: 'identifier',
    presence: 'Optional',
    meaning: "The person's identifier in this system, where the document records one."
  },
  {
    path: 'quote/contact/name',
    type: 'text',
    presence: 'Optional',
    meaning: "The person's name."
  },
  {
    path: 'quote/contact/jobTitle',
    type: 'text',
    presence: 'Optional',
    meaning: "The person's job title, where one is recorded against them."
  },
  {
    path: 'quote/contact/email',
    type: 'text',
    presence: 'Optional',
    meaning: "The person's email address."
  },
  {
    path: 'quote/contact/phone',
    type: 'text',
    presence: 'Optional',
    meaning: "The person's telephone number."
  },
  {
    path: 'quote/assignedTo',
    type: 'group',
    presence: 'Optional',
    meaning:
      'The user the quote is assigned to, which is what the Information page calls the assigned author and whose details the issued PDFs print.'
  },
  {
    path: 'quote/assignedTo/id',
    type: 'identifier',
    presence: 'Optional',
    meaning: "The person's identifier in this system, where the document records one."
  },
  {
    path: 'quote/assignedTo/name',
    type: 'text',
    presence: 'Optional',
    meaning: "The person's name."
  },
  {
    path: 'quote/assignedTo/jobTitle',
    type: 'text',
    presence: 'Optional',
    meaning: "The person's job title, where one is recorded against them."
  },
  {
    path: 'quote/assignedTo/email',
    type: 'text',
    presence: 'Optional',
    meaning: "The person's email address."
  },
  {
    path: 'quote/assignedTo/phone',
    type: 'text',
    presence: 'Optional',
    meaning: "The person's telephone number."
  },
  {
    path: 'quote/project',
    type: 'group',
    presence: 'Optional',
    meaning: 'A group of related fields.'
  },
  {
    path: 'quote/project/id',
    type: 'identifier',
    presence: 'Optional',
    meaning: "The project's identifier in this system."
  },
  {
    path: 'quote/project/number',
    type: 'text',
    presence: 'Optional',
    meaning: 'The project number as shown in the application.'
  },
  {
    path: 'quote/project/title',
    type: 'text',
    presence: 'Optional',
    meaning: 'The project title.'
  },
  {
    path: 'quote/dealership',
    type: 'group',
    presence: 'Optional',
    meaning: 'The supplier the frames on this quote are ordered from.'
  },
  {
    path: 'quote/dealership/name',
    type: 'text',
    presence: 'Optional',
    meaning: "The dealership's trading name."
  },
  {
    path: 'quote/dealership/phone',
    type: 'text',
    presence: 'Optional',
    meaning: 'The general telephone number for the dealership.'
  },
  {
    path: 'quote/dealership/email',
    type: 'text',
    presence: 'Optional',
    meaning: 'The general email address for the dealership.'
  },
  {
    path: 'quote/dealership/website',
    type: 'text',
    presence: 'Optional',
    meaning: "The dealership's website address."
  },
  {
    path: 'quote/dealership/address',
    type: 'group',
    presence: 'Optional',
    meaning: 'A group of related fields.'
  },
  {
    path: 'quote/dealership/address/source',
    type: 'text',
    presence: 'Optional',
    meaning:
      'Which address the user chose: the project address, the client address, the branch address or one typed in by hand. A dealer mapping delivery details usually needs this as well as the lines themselves.'
  },
  {
    path: 'quote/dealership/address/line1',
    type: 'text',
    presence: 'Optional',
    meaning: 'The first line of the address.'
  },
  {
    path: 'quote/dealership/address/line2',
    type: 'text',
    presence: 'Optional',
    meaning: 'The second line of the address, where there is one.'
  },
  {
    path: 'quote/dealership/address/line3',
    type: 'text',
    presence: 'Optional',
    meaning: 'The third line of the address, where there is one.'
  },
  {
    path: 'quote/dealership/address/line4',
    type: 'text',
    presence: 'Optional',
    meaning: 'The fourth line of the address, where there is one.'
  },
  {
    path: 'quote/dealership/address/locality',
    type: 'text',
    presence: 'Optional',
    meaning: 'Town or city.'
  },
  {
    path: 'quote/dealership/address/region',
    type: 'text',
    presence: 'Optional',
    meaning: 'State, province or county.'
  },
  {
    path: 'quote/dealership/address/postcode',
    type: 'text',
    presence: 'Optional',
    meaning: 'The postcode or zip code.'
  },
  {
    path: 'quote/dealership/address/country',
    type: 'text',
    presence: 'Optional',
    meaning: 'The country.'
  },
  {
    path: 'quote/dealership/address/notes',
    type: 'text',
    presence: 'Optional',
    meaning: 'Free text the user entered about getting the delivery to this address.'
  },
  {
    path: 'quote/supplier',
    type: 'text',
    presence: 'Optional',
    meaning: 'The supplier the frames on this quote are ordered from.'
  },
  {
    path: 'quote/deliveryAddress',
    type: 'group',
    presence: 'Optional',
    meaning: 'A group of related fields.'
  },
  {
    path: 'quote/deliveryAddress/source',
    type: 'text',
    presence: 'Optional',
    meaning:
      'Which address the user chose: the project address, the client address, the branch address or one typed in by hand. A dealer mapping delivery details usually needs this as well as the lines themselves.'
  },
  {
    path: 'quote/deliveryAddress/line1',
    type: 'text',
    presence: 'Optional',
    meaning: 'The first line of the address.'
  },
  {
    path: 'quote/deliveryAddress/line2',
    type: 'text',
    presence: 'Optional',
    meaning: 'The second line of the address, where there is one.'
  },
  {
    path: 'quote/deliveryAddress/line3',
    type: 'text',
    presence: 'Optional',
    meaning: 'The third line of the address, where there is one.'
  },
  {
    path: 'quote/deliveryAddress/line4',
    type: 'text',
    presence: 'Optional',
    meaning: 'The fourth line of the address, where there is one.'
  },
  {
    path: 'quote/deliveryAddress/locality',
    type: 'text',
    presence: 'Optional',
    meaning: 'Town or city.'
  },
  {
    path: 'quote/deliveryAddress/region',
    type: 'text',
    presence: 'Optional',
    meaning: 'State, province or county.'
  },
  {
    path: 'quote/deliveryAddress/postcode',
    type: 'text',
    presence: 'Optional',
    meaning: 'The postcode or zip code.'
  },
  {
    path: 'quote/deliveryAddress/country',
    type: 'text',
    presence: 'Optional',
    meaning: 'The country.'
  },
  {
    path: 'quote/deliveryAddress/notes',
    type: 'text',
    presence: 'Optional',
    meaning: 'Free text the user entered about getting the delivery to this address.'
  },
  {
    path: 'lineItems/lineItem',
    type: 'list',
    presence: 'Always',
    meaning:
      'One entry per row of the quote items list, carrying the values of the single line row only. Nothing that is visible only once a row is expanded appears here.'
  },
  {
    path: 'lineItems/lineItem/itemNumber',
    type: 'text',
    presence: 'Optional',
    meaning:
      'The number shown in the No. column. Once a quote uses item groups this restarts at 1 in each group, so it is NOT unique across the quote. Key on Sequence instead.'
  },
  {
    path: 'lineItems/lineItem/sequence',
    type: 'number',
    presence: 'Always',
    meaning: "The line's position in the quote. Unique, and safe to sort and key on."
  },
  {
    path: 'lineItems/lineItem/lineType',
    type: 'text',
    presence: 'Always',
    meaning:
      'What kind of line this is: frame, specialItem, buyIn, freehand, shipping, ssi or priceAdjustment. A receiving system usually routes on this.'
  },
  {
    path: 'lineItems/lineItem/buyIn',
    type: 'true or false',
    presence: 'Always',
    meaning:
      'True for a buy-in line: stock the dealer supplies themselves rather than ordering from the supplier. Kept separate from LineType because a buy-in is still a frame or a special item, and collapsing the two would lose whichever the reader happened to need.'
  },
  {
    path: 'lineItems/lineItem/location',
    type: 'text',
    presence: 'Optional',
    meaning: "The item's location or title, the first line of the Location column."
  },
  {
    path: 'lineItems/lineItem/description',
    type: 'text',
    presence: 'Optional',
    meaning: 'The item description, as entered on the quote.'
  },
  {
    path: 'lineItems/lineItem/optionsSummary',
    type: 'text',
    presence: 'Optional',
    meaning:
      'The one line summary shown in the Options column, for example the frame dimensions. This is a summary for a human to read, not a structured option list.'
  },
  {
    path: 'lineItems/lineItem/group',
    type: 'group',
    presence: 'Optional',
    meaning: 'The item group this line sits in, shown in the Tags column. Absent when the quote is not grouped.'
  },
  {
    path: 'lineItems/lineItem/group/id',
    type: 'identifier',
    presence: 'Optional',
    meaning: "The group's identifier, stable across every line in the same group."
  },
  {
    path: 'lineItems/lineItem/group/name',
    type: 'text',
    presence: 'Optional',
    meaning: 'The name of the group, as shown in the Tags column.'
  },
  {
    path: 'lineItems/lineItem/quantity',
    type: 'number',
    presence: 'Optional',
    meaning: 'How many of this item the line covers.'
  },
  {
    path: 'lineItems/lineItem/lastModifiedDate',
    type: 'timestamp (ISO 8601 with offset)',
    presence: 'Optional',
    meaning: 'When this line was last changed before the quote was issued.'
  },
  {
    path: 'lineItems/lineItem/notesToClient',
    type: 'text',
    presence: 'Optional',
    meaning: 'The note to the client shown against this line, where there is one.'
  },
  {
    path: 'lineItems/lineItem/notesToManufacturer',
    type: 'text',
    presence: 'Optional',
    meaning: 'The note to the manufacturer against this line, where there is one.'
  },
  {
    path: 'lineItems/lineItem/productLine',
    type: 'text',
    presence: 'Optional',
    meaning: 'The supplier product line, for frames that carry one.'
  },
  {
    path: 'lineItems/lineItem/imageUrl',
    type: 'text',
    presence: 'Optional',
    meaning:
      'A link to the item image. A link, never the image itself: embedding the picture would make the file tens of megabytes and unusable for the job it is for.'
  },
  {
    path: 'lineItems/lineItem/thumbnailUrl',
    type: 'text',
    presence: 'Optional',
    meaning: 'A link to a small preview image of the item.'
  },
  {
    path: 'lineItems/lineItem/pricing',
    type: 'group',
    presence: 'Always',
    meaning: 'A group of related fields.'
  },
  {
    path: 'lineItems/lineItem/pricing/listPrice',
    type: 'number',
    presence: 'Optional',
    meaning: "The supplier's list price for one unit, before any multiplier."
  },
  {
    path: 'lineItems/lineItem/pricing/extendedListPrice',
    type: 'number',
    presence: 'Optional',
    meaning: 'List price for the whole line: list price times quantity.'
  },
  {
    path: 'lineItems/lineItem/pricing/buyingMultiplier',
    type: 'number',
    presence: 'Optional',
    meaning:
      'The buying multiplier applied to the list price, as a factor. 0.65 means the dealer buys at 65 percent of list.'
  },
  {
    path: 'lineItems/lineItem/pricing/supplierPriceAdjustment',
    type: 'number',
    presence: 'Optional',
    meaning: 'An adjustment the supplier applied to this line.'
  },
  {
    path: 'lineItems/lineItem/pricing/dealerCost',
    type: 'number',
    presence: 'Optional',
    meaning: 'What the line costs the dealer, including any supplier adjustment.'
  },
  {
    path: 'lineItems/lineItem/pricing/marginPercent',
    type: 'number',
    presence: 'Optional',
    meaning: 'The margin or markup on the line, as a percentage. 35 means 35 percent, not 0.35.'
  },
  {
    path: 'lineItems/lineItem/pricing/priceAdjustment',
    type: 'number',
    presence: 'Optional',
    meaning: 'An adjustment the dealer applied to this line.'
  },
  {
    path: 'lineItems/lineItem/pricing/tax',
    type: 'number',
    presence: 'Optional',
    meaning:
      'The tax on this line. Zero when the line is not taxable; see Taxable to tell a genuine zero apart from an exempt line.'
  },
  {
    path: 'lineItems/lineItem/pricing/taxable',
    type: 'true or false',
    presence: 'Always',
    meaning: 'False when the line is excluded from tax.'
  },
  {
    path: 'lineItems/lineItem/pricing/sellingPrice',
    type: 'number',
    presence: 'Optional',
    meaning: 'What the client is charged for the line, before tax.'
  },
  {
    path: 'terms',
    type: 'group',
    presence: 'Always',
    meaning: 'A group of related fields.'
  },
  {
    path: 'terms/quoteTerms',
    type: 'text',
    presence: 'Optional',
    meaning: "The dealer's terms and conditions, as shown on the quote Terms tab."
  },
  {
    path: 'terms/supplierPurchaseOrderTerms',
    type: 'text',
    presence: 'Optional',
    meaning: "The supplier's purchase order terms and conditions."
  },
  {
    path: 'calculations/calculationGroup',
    type: 'list',
    presence: 'Always',
    meaning:
      'The supplier calculation groups, in the order the supplier returned them. Absent or empty when the quote has none; that is a normal state, not an error.'
  },
  {
    path: 'calculations/calculationGroup/name',
    type: 'text',
    presence: 'Optional',
    meaning: 'The name of the calculation group, as the supplier labelled it.'
  },
  {
    path: 'calculations/calculationGroup/values/calculationValue',
    type: 'list',
    presence: 'Always',
    meaning: 'A repeating group; its fields follow.'
  },
  {
    path: 'calculations/calculationGroup/values/calculationValue/label',
    type: 'text',
    presence: 'Optional',
    meaning: 'The name of the value, as the supplier labelled it.'
  },
  {
    path: 'calculations/calculationGroup/values/calculationValue/value',
    type: 'text',
    presence: 'Optional',
    meaning:
      'The value, as text. Suppliers define these themselves, so one may be a number, a measurement or a word, and the format varies between suppliers.'
  },
  {
    path: 'priceSummary',
    type: 'group',
    presence: 'Always',
    meaning: 'A group of related fields.'
  },
  {
    path: 'priceSummary/dealerCost',
    type: 'number',
    presence: 'Always',
    meaning: 'What the whole quote costs the dealer, before any margin.'
  },
  {
    path: 'priceSummary/priceAdjustment',
    type: 'number',
    presence: 'Always',
    meaning: 'Adjustments applied to the quote as a whole rather than to any one line.'
  },
  {
    path: 'priceSummary/subtotal',
    type: 'number',
    presence: 'Always',
    meaning: 'The tax on the quote.'
  },
  {
    path: 'priceSummary/tax',
    type: 'number',
    presence: 'Always',
    meaning: 'The quote total, including tax. This is the figure the client is quoted.'
  },
  {
    path: 'priceSummary/total',
    type: 'number',
    presence: 'Always',
    meaning: 'The quote total including tax. This is the figure the client is quoted.'
  }
];

export const purchaseOrderExportFields: ExportFieldReferenceEntry[] = [
  {
    path: 'schemaVersion',
    type: 'text',
    presence: 'Always',
    meaning: 'The contract version this file was written against.'
  },
  {
    path: 'documentType',
    type: 'text',
    presence: 'Always',
    meaning: '"quote" or "purchaseOrder". Lets one reader route both kinds of file.'
  },
  {
    path: 'generatedUtc',
    type: 'timestamp (ISO 8601 with offset)',
    presence: 'Always',
    meaning:
      'When the file was written, which is when the resource was issued. This is not the download time: the file is written once and never rewritten.'
  },
  {
    path: 'resourceId',
    type: 'identifier',
    presence: 'Always',
    meaning: 'The identifier of the resource this file was generated from.'
  },
  {
    path: 'documentNumber',
    type: 'text',
    presence: 'Optional',
    meaning: 'The human readable number of the document, as shown in the application.'
  },
  {
    path: 'currency',
    type: 'text',
    presence: 'Always',
    meaning:
      'The currency every monetary value in this file is expressed in. Money is published as a bare number; the currency is stated once, here.'
  },
  {
    path: 'purchaseOrder',
    type: 'group',
    presence: 'Always',
    meaning: 'A group of related fields.'
  },
  {
    path: 'purchaseOrder/number',
    type: 'text',
    presence: 'Optional',
    meaning: 'The purchase order number as shown in the application.'
  },
  {
    path: 'purchaseOrder/customNumber',
    type: 'text',
    presence: 'Optional',
    meaning: "The dealer's own reference for this order, where one was entered."
  },
  {
    path: 'purchaseOrder/title',
    type: 'text',
    presence: 'Optional',
    meaning: 'The order title.'
  },
  {
    path: 'purchaseOrder/description',
    type: 'text',
    presence: 'Optional',
    meaning: 'The order description.'
  },
  {
    path: 'purchaseOrder/issuedDate',
    type: 'date (yyyy-MM-dd)',
    presence: 'Optional',
    meaning: 'The date the order was issued to the supplier.'
  },
  {
    path: 'purchaseOrder/proposedInstallationDate',
    type: 'date (yyyy-MM-dd)',
    presence: 'Optional',
    meaning: 'The proposed installation date for the work this order supplies.'
  },
  {
    path: 'purchaseOrder/proposedDeliveryDate',
    type: 'date (yyyy-MM-dd)',
    presence: 'Optional',
    meaning: 'The delivery date proposed on the order.'
  },
  {
    path: 'purchaseOrder/supplier',
    type: 'text',
    presence: 'Optional',
    meaning: 'The supplier this order is placed with.'
  },
  {
    path: 'purchaseOrder/supplierReference',
    type: 'text',
    presence: 'Optional',
    meaning:
      "The supplier's own reference for this order, returned by their system when the order was accepted. This is usually the key a supplier looks the order up by."
  },
  {
    path: 'purchaseOrder/supplierStatus',
    type: 'text',
    presence: 'Optional',
    meaning: "The progress status the supplier's system reports for this order."
  },
  {
    path: 'purchaseOrder/supplierDeliveryDate',
    type: 'date (yyyy-MM-dd)',
    presence: 'Optional',
    meaning: "The delivery date the supplier's system reports."
  },
  {
    path: 'purchaseOrder/supplierClosedDate',
    type: 'date (yyyy-MM-dd)',
    presence: 'Optional',
    meaning: "The date the supplier's system closed the order, where it has."
  },
  {
    path: 'purchaseOrder/supplierShippingLeadTimeDays',
    type: 'number',
    presence: 'Optional',
    meaning: 'The shipping lead time the supplier quoted, in days.'
  },
  {
    path: 'purchaseOrder/assignedTo',
    type: 'group',
    presence: 'Optional',
    meaning: 'The user the order is assigned to.'
  },
  {
    path: 'purchaseOrder/assignedTo/id',
    type: 'identifier',
    presence: 'Optional',
    meaning: "The person's identifier in this system, where the document records one."
  },
  {
    path: 'purchaseOrder/assignedTo/name',
    type: 'text',
    presence: 'Optional',
    meaning: "The person's name."
  },
  {
    path: 'purchaseOrder/assignedTo/jobTitle',
    type: 'text',
    presence: 'Optional',
    meaning: "The person's job title, where one is recorded against them."
  },
  {
    path: 'purchaseOrder/assignedTo/email',
    type: 'text',
    presence: 'Optional',
    meaning: "The person's email address."
  },
  {
    path: 'purchaseOrder/assignedTo/phone',
    type: 'text',
    presence: 'Optional',
    meaning: "The person's telephone number."
  },
  {
    path: 'purchaseOrder/project',
    type: 'group',
    presence: 'Optional',
    meaning: 'A group of related fields.'
  },
  {
    path: 'purchaseOrder/project/id',
    type: 'identifier',
    presence: 'Optional',
    meaning: "The project's identifier in this system."
  },
  {
    path: 'purchaseOrder/project/number',
    type: 'text',
    presence: 'Optional',
    meaning: 'The project number as shown in the application.'
  },
  {
    path: 'purchaseOrder/project/title',
    type: 'text',
    presence: 'Optional',
    meaning: 'The project title.'
  },
  {
    path: 'purchaseOrder/dealership',
    type: 'group',
    presence: 'Optional',
    meaning: 'A group of related fields.'
  },
  {
    path: 'purchaseOrder/dealership/name',
    type: 'text',
    presence: 'Optional',
    meaning: "The dealership's trading name."
  },
  {
    path: 'purchaseOrder/dealership/phone',
    type: 'text',
    presence: 'Optional',
    meaning: 'The general telephone number for the dealership.'
  },
  {
    path: 'purchaseOrder/dealership/email',
    type: 'text',
    presence: 'Optional',
    meaning: 'The general email address for the dealership.'
  },
  {
    path: 'purchaseOrder/dealership/website',
    type: 'text',
    presence: 'Optional',
    meaning: "The dealership's website address."
  },
  {
    path: 'purchaseOrder/dealership/address',
    type: 'group',
    presence: 'Optional',
    meaning: 'A group of related fields.'
  },
  {
    path: 'purchaseOrder/dealership/address/source',
    type: 'text',
    presence: 'Optional',
    meaning:
      'Which address the user chose: the project address, the client address, the branch address or one typed in by hand. A dealer mapping delivery details usually needs this as well as the lines themselves.'
  },
  {
    path: 'purchaseOrder/dealership/address/line1',
    type: 'text',
    presence: 'Optional',
    meaning: 'The first line of the address.'
  },
  {
    path: 'purchaseOrder/dealership/address/line2',
    type: 'text',
    presence: 'Optional',
    meaning: 'The second line of the address, where there is one.'
  },
  {
    path: 'purchaseOrder/dealership/address/line3',
    type: 'text',
    presence: 'Optional',
    meaning: 'The third line of the address, where there is one.'
  },
  {
    path: 'purchaseOrder/dealership/address/line4',
    type: 'text',
    presence: 'Optional',
    meaning: 'The fourth line of the address, where there is one.'
  },
  {
    path: 'purchaseOrder/dealership/address/locality',
    type: 'text',
    presence: 'Optional',
    meaning: 'Town or city.'
  },
  {
    path: 'purchaseOrder/dealership/address/region',
    type: 'text',
    presence: 'Optional',
    meaning: 'State, province or county.'
  },
  {
    path: 'purchaseOrder/dealership/address/postcode',
    type: 'text',
    presence: 'Optional',
    meaning: 'The postcode or zip code.'
  },
  {
    path: 'purchaseOrder/dealership/address/country',
    type: 'text',
    presence: 'Optional',
    meaning: 'The country.'
  },
  {
    path: 'purchaseOrder/dealership/address/notes',
    type: 'text',
    presence: 'Optional',
    meaning: 'Free text the user entered about getting the delivery to this address.'
  },
  {
    path: 'purchaseOrder/deliveryAddress',
    type: 'group',
    presence: 'Optional',
    meaning: 'A group of related fields.'
  },
  {
    path: 'purchaseOrder/deliveryAddress/source',
    type: 'text',
    presence: 'Optional',
    meaning:
      'Which address the user chose: the project address, the client address, the branch address or one typed in by hand. A dealer mapping delivery details usually needs this as well as the lines themselves.'
  },
  {
    path: 'purchaseOrder/deliveryAddress/line1',
    type: 'text',
    presence: 'Optional',
    meaning: 'The first line of the address.'
  },
  {
    path: 'purchaseOrder/deliveryAddress/line2',
    type: 'text',
    presence: 'Optional',
    meaning: 'The second line of the address, where there is one.'
  },
  {
    path: 'purchaseOrder/deliveryAddress/line3',
    type: 'text',
    presence: 'Optional',
    meaning: 'The third line of the address, where there is one.'
  },
  {
    path: 'purchaseOrder/deliveryAddress/line4',
    type: 'text',
    presence: 'Optional',
    meaning: 'The fourth line of the address, where there is one.'
  },
  {
    path: 'purchaseOrder/deliveryAddress/locality',
    type: 'text',
    presence: 'Optional',
    meaning: 'Town or city.'
  },
  {
    path: 'purchaseOrder/deliveryAddress/region',
    type: 'text',
    presence: 'Optional',
    meaning: 'State, province or county.'
  },
  {
    path: 'purchaseOrder/deliveryAddress/postcode',
    type: 'text',
    presence: 'Optional',
    meaning: 'The postcode or zip code.'
  },
  {
    path: 'purchaseOrder/deliveryAddress/country',
    type: 'text',
    presence: 'Optional',
    meaning: 'The country.'
  },
  {
    path: 'purchaseOrder/deliveryAddress/notes',
    type: 'text',
    presence: 'Optional',
    meaning: 'Free text the user entered about getting the delivery to this address.'
  },
  {
    path: 'lineItems/lineItem',
    type: 'list',
    presence: 'Always',
    meaning:
      'One entry per order line, carrying the values of the single line row only. Lines appear in the order they are shown on the order, and each names the delivery batch it belongs to.'
  },
  {
    path: 'lineItems/lineItem/itemNumber',
    type: 'text',
    presence: 'Optional',
    meaning:
      'The number shown in the No. column. Numbering restarts within each delivery batch, so it is NOT unique across the order. Key on Sequence instead.'
  },
  {
    path: 'lineItems/lineItem/sequence',
    type: 'number',
    presence: 'Always',
    meaning: "The line's position in the order. Unique, and safe to sort and key on."
  },
  {
    path: 'lineItems/lineItem/lineType',
    type: 'text',
    presence: 'Always',
    meaning:
      'What kind of line this is: frame, specialItem, buyIn, shipping or ssi. Freehand lines and dealer price adjustments never reach a purchase order.'
  },
  {
    path: 'lineItems/lineItem/batchId',
    type: 'identifier',
    presence: 'Optional',
    meaning: 'The delivery batch this line belongs to. Absent when the order is not batched.'
  },
  {
    path: 'lineItems/lineItem/batchName',
    type: 'text',
    presence: 'Optional',
    meaning: 'The name of the delivery batch this line belongs to.'
  },
  {
    path: 'lineItems/lineItem/title',
    type: 'text',
    presence: 'Optional',
    meaning: "The item's title, the first line of the item column."
  },
  {
    path: 'lineItems/lineItem/description',
    type: 'text',
    presence: 'Optional',
    meaning: 'The item description.'
  },
  {
    path: 'lineItems/lineItem/optionsSummary',
    type: 'text',
    presence: 'Optional',
    meaning: 'The one line summary shown in the Options column, for example the dimensions.'
  },
  {
    path: 'lineItems/lineItem/quantity',
    type: 'number',
    presence: 'Optional',
    meaning: 'How many of this item the line covers.'
  },
  {
    path: 'lineItems/lineItem/notesToManufacturer',
    type: 'text',
    presence: 'Optional',
    meaning: 'The note to the manufacturer against this line, where there is one.'
  },
  {
    path: 'lineItems/lineItem/productLine',
    type: 'text',
    presence: 'Optional',
    meaning: 'The supplier product line, for frames that carry one.'
  },
  {
    path: 'lineItems/lineItem/imageUrl',
    type: 'text',
    presence: 'Optional',
    meaning: 'A link to the item image. A link, never the image itself.'
  },
  {
    path: 'lineItems/lineItem/thumbnailUrl',
    type: 'text',
    presence: 'Optional',
    meaning: 'A link to a small preview image of the item.'
  },
  {
    path: 'lineItems/lineItem/price',
    type: 'number',
    presence: 'Optional',
    meaning:
      'What the dealer is paying the supplier for this line, in the currency named once on the document envelope. A purchase order carries the buying side only; there is no selling price or margin on an order.'
  },
  {
    path: 'deliveryBatches/deliveryBatch',
    type: 'list',
    presence: 'Always',
    meaning:
      'The delivery batches the order is organised into, with the lines that belong to each. Absent or empty when the order is not batched.'
  },
  {
    path: 'deliveryBatches/deliveryBatch/id',
    type: 'identifier',
    presence: 'Optional',
    meaning: "The batch's identifier. Line items reference this in their batchId."
  },
  {
    path: 'deliveryBatches/deliveryBatch/name',
    type: 'text',
    presence: 'Optional',
    meaning: 'The name of the delivery batch.'
  },
  {
    path: 'deliveryBatches/deliveryBatch/detail',
    type: 'text',
    presence: 'Optional',
    meaning:
      'The single line supplement the order page prints after the batch name, usually the delivery date and customer reference the supplier ships against. Already formatted for reading.'
  },
  {
    path: 'deliveryBatches/deliveryBatch/subtotal',
    type: 'number',
    presence: 'Always',
    meaning: 'The batch total.'
  },
  {
    path: 'deliveryBatches/deliveryBatch/lineSequences/sequence',
    type: 'number',
    presence: 'Always',
    meaning:
      'The sequence numbers of the lines in this batch, in order. Cross reference these against lineItem/sequence; the lines themselves are not repeated here.'
  },
  {
    path: 'supplierPurchaseOrderTerms',
    type: 'text',
    presence: 'Optional',
    meaning: "The supplier's purchase order terms and conditions."
  },
  {
    path: 'priceSummary',
    type: 'group',
    presence: 'Always',
    meaning: 'A group of related fields.'
  },
  {
    path: 'priceSummary/netTotal',
    type: 'number',
    presence: 'Always',
    meaning: 'The sum of the line prices, before any order level adjustment.'
  },
  {
    path: 'priceSummary/adjustmentTotal',
    type: 'number',
    presence: 'Always',
    meaning: 'Order level adjustments.'
  },
  {
    path: 'priceSummary/total',
    type: 'number',
    presence: 'Always',
    meaning: 'The order total: net total plus adjustments.'
  }
];

export const quoteExportSampleJson = `{
  "schemaVersion": "1.0",
  "documentType": "quote",
  "generatedUtc": "2026-09-23T14:05:09+00:00",
  "resourceId": "11111111-1111-1111-1111-111111111111",
  "documentNumber": "Q-1042",
  "currency": "USD",
  "quote": {
    "number": "Q-1042",
    "customNumber": "PO-INT-88",
    "title": "Façade phase 2 – Müller residence 住宅",
    "description": "Ground floor glazing",
    "quoteType": "Standard",
    "budget": 125000,
    "createdDate": "2026-08-01T09:12:00+00:00",
    "lastModifiedDate": "2026-09-23T13:58:41+00:00",
    "issuedDate": "2026-09-23",
    "validUntilDate": "2026-10-23",
    "proposedDeliveryDate": "2026-12-01",
    "client": {
      "id": "22222222-2222-2222-2222-222222222222",
      "name": "Müller Developments",
      "clientType": "Trade 30 day"
    },
    "contact": {
      "name": "Anna Müller",
      "email": "anna@example.test"
    },
    "assignedTo": {
      "name": "Sam Dealer",
      "jobTitle": "Estimator"
    },
    "project": {
      "number": "P-77",
      "title": "Riverside"
    },
    "dealership": {
      "name": "Acme Windows",
      "address": {
        "line1": "1 Mill Road",
        "locality": "Leeds",
        "postcode": "LS1 1AA"
      }
    },
    "supplier": "Northern Frames",
    "deliveryAddress": {
      "source": "Project",
      "line1": "14 Riverside Way",
      "locality": "Leeds",
      "postcode": "LS2 7EE",
      "notes": "Crane access before 09:00 only"
    }
  },
  "lineItems": [
    {
      "itemNumber": "1",
      "sequence": 1,
      "lineType": "frame",
      "buyIn": false,
      "location": "Kitchen",
      "description": "Casement, 2 light",
      "optionsSummary": "1200 x 900",
      "group": {
        "id": "33333333-3333-3333-3333-333333333333",
        "name": "Phase 1"
      },
      "quantity": 4,
      "lastModifiedDate": "2026-09-20T11:00:00+00:00",
      "notesToClient": "Colour confirmed",
      "productLine": "NF-200",
      "thumbnailUrl": "https://example.test/thumb/1.png",
      "pricing": {
        "listPrice": 500,
        "extendedListPrice": 2000,
        "buyingMultiplier": 0.65,
        "supplierPriceAdjustment": -50,
        "dealerCost": 1250,
        "marginPercent": 35,
        "priceAdjustment": 0,
        "tax": 192.31,
        "taxable": true,
        "sellingPrice": 1923.08
      }
    },
    {
      "itemNumber": "2",
      "sequence": 2,
      "lineType": "shipping",
      "buyIn": false,
      "location": "Delivery",
      "quantity": 1,
      "pricing": {
        "tax": 0,
        "taxable": false,
        "sellingPrice": 150
      }
    }
  ],
  "terms": {
    "quoteTerms": "Payment due 30 days from invoice.",
    "supplierPurchaseOrderTerms": "Goods remain the property of the supplier until paid."
  },
  "calculations": [
    {
      "name": "Thermal",
      "values": [
        {
          "label": "U value",
          "value": "1.24"
        },
        {
          "label": "Total area",
          "value": "18.40"
        }
      ]
    }
  ],
  "priceSummary": {
    "dealerCost": 1250,
    "priceAdjustment": -25.50,
    "subtotal": 2073.08,
    "tax": 192.31,
    "total": 2265.39
  }
}`;

export const quoteExportSampleXml = `<?xml version="1.0" encoding="utf-8"?>
<quoteExport>
  <schemaVersion>1.0</schemaVersion>
  <documentType>quote</documentType>
  <generatedUtc>2026-09-23T14:05:09+00:00</generatedUtc>
  <resourceId>11111111-1111-1111-1111-111111111111</resourceId>
  <documentNumber>Q-1042</documentNumber>
  <currency>USD</currency>
  <quote>
    <number>Q-1042</number>
    <customNumber>PO-INT-88</customNumber>
    <title>Façade phase 2 – Müller residence 住宅</title>
    <description>Ground floor glazing</description>
    <quoteType>Standard</quoteType>
    <budget>125000</budget>
    <createdDate>2026-08-01T09:12:00+00:00</createdDate>
    <lastModifiedDate>2026-09-23T13:58:41+00:00</lastModifiedDate>
    <issuedDate>2026-09-23</issuedDate>
    <validUntilDate>2026-10-23</validUntilDate>
    <proposedDeliveryDate>2026-12-01</proposedDeliveryDate>
    <client>
      <id>22222222-2222-2222-2222-222222222222</id>
      <name>Müller Developments</name>
      <clientType>Trade 30 day</clientType>
    </client>
    <contact>
      <name>Anna Müller</name>
      <email>anna@example.test</email>
    </contact>
    <assignedTo>
      <name>Sam Dealer</name>
      <jobTitle>Estimator</jobTitle>
    </assignedTo>
    <project>
      <number>P-77</number>
      <title>Riverside</title>
    </project>
    <dealership>
      <name>Acme Windows</name>
      <address>
        <line1>1 Mill Road</line1>
        <locality>Leeds</locality>
        <postcode>LS1 1AA</postcode>
      </address>
    </dealership>
    <supplier>Northern Frames</supplier>
    <deliveryAddress>
      <source>Project</source>
      <line1>14 Riverside Way</line1>
      <locality>Leeds</locality>
      <postcode>LS2 7EE</postcode>
      <notes>Crane access before 09:00 only</notes>
    </deliveryAddress>
  </quote>
  <lineItems>
    <lineItem>
      <itemNumber>1</itemNumber>
      <sequence>1</sequence>
      <lineType>frame</lineType>
      <buyIn>false</buyIn>
      <location>Kitchen</location>
      <description>Casement, 2 light</description>
      <optionsSummary>1200 x 900</optionsSummary>
      <group>
        <id>33333333-3333-3333-3333-333333333333</id>
        <name>Phase 1</name>
      </group>
      <quantity>4</quantity>
      <lastModifiedDate>2026-09-20T11:00:00+00:00</lastModifiedDate>
      <notesToClient>Colour confirmed</notesToClient>
      <productLine>NF-200</productLine>
      <thumbnailUrl>https://example.test/thumb/1.png</thumbnailUrl>
      <pricing>
        <listPrice>500</listPrice>
        <extendedListPrice>2000</extendedListPrice>
        <buyingMultiplier>0.65</buyingMultiplier>
        <supplierPriceAdjustment>-50</supplierPriceAdjustment>
        <dealerCost>1250</dealerCost>
        <marginPercent>35</marginPercent>
        <priceAdjustment>0</priceAdjustment>
        <tax>192.31</tax>
        <taxable>true</taxable>
        <sellingPrice>1923.08</sellingPrice>
      </pricing>
    </lineItem>
    <lineItem>
      <itemNumber>2</itemNumber>
      <sequence>2</sequence>
      <lineType>shipping</lineType>
      <buyIn>false</buyIn>
      <location>Delivery</location>
      <quantity>1</quantity>
      <pricing>
        <tax>0</tax>
        <taxable>false</taxable>
        <sellingPrice>150</sellingPrice>
      </pricing>
    </lineItem>
  </lineItems>
  <terms>
    <quoteTerms>Payment due 30 days from invoice.</quoteTerms>
    <supplierPurchaseOrderTerms>Goods remain the property of the supplier until paid.</supplierPurchaseOrderTerms>
  </terms>
  <calculations>
    <calculationGroup>
      <name>Thermal</name>
      <values>
        <calculationValue>
          <label>U value</label>
          <value>1.24</value>
        </calculationValue>
        <calculationValue>
          <label>Total area</label>
          <value>18.40</value>
        </calculationValue>
      </values>
    </calculationGroup>
  </calculations>
  <priceSummary>
    <dealerCost>1250</dealerCost>
    <priceAdjustment>-25.50</priceAdjustment>
    <subtotal>2073.08</subtotal>
    <tax>192.31</tax>
    <total>2265.39</total>
  </priceSummary>
</quoteExport>`;

export const purchaseOrderExportSampleJson = `{
  "schemaVersion": "1.0",
  "documentType": "purchaseOrder",
  "generatedUtc": "2026-09-23T15:20:00+00:00",
  "resourceId": "44444444-4444-4444-4444-444444444444",
  "documentNumber": "PO-2210",
  "currency": "USD",
  "purchaseOrder": {
    "number": "PO-2210",
    "customNumber": "ACME-55",
    "title": "Façade phase 2 – Müller residence 住宅",
    "issuedDate": "2026-09-23",
    "proposedInstallationDate": "2026-12-01",
    "supplier": "Northern Frames",
    "supplierReference": "NF-99871",
    "supplierStatus": "Accepted",
    "supplierDeliveryDate": "2026-11-20",
    "supplierShippingLeadTimeDays": 14,
    "assignedTo": {
      "name": "Kim Lead"
    },
    "project": {
      "number": "P-77",
      "title": "Riverside"
    },
    "dealership": {
      "name": "Acme Windows"
    },
    "deliveryAddress": {
      "source": "Project",
      "line1": "14 Riverside Way"
    }
  },
  "lineItems": [
    {
      "itemNumber": "1",
      "sequence": 1,
      "lineType": "frame",
      "batchId": "55555555-5555-5555-5555-555555555555",
      "batchName": "Batch A",
      "title": "Kitchen",
      "description": "Casement, 2 light",
      "optionsSummary": "1200 x 900",
      "quantity": 4,
      "productLine": "NF-200",
      "price": 1250
    },
    {
      "itemNumber": "1",
      "sequence": 2,
      "lineType": "specialItem",
      "batchId": "66666666-6666-6666-6666-666666666666",
      "batchName": "Batch B",
      "title": "Trim set",
      "quantity": 1,
      "price": 90
    }
  ],
  "deliveryBatches": [
    {
      "id": "55555555-5555-5555-5555-555555555555",
      "name": "Batch A",
      "detail": "Deliver 2026-11-20",
      "subtotal": 1250,
      "lineSequences": [
        1
      ]
    },
    {
      "id": "66666666-6666-6666-6666-666666666666",
      "name": "Batch B",
      "subtotal": 90,
      "lineSequences": [
        2
      ]
    }
  ],
  "supplierPurchaseOrderTerms": "Goods remain the property of the supplier until paid.",
  "priceSummary": {
    "netTotal": 1340,
    "adjustmentTotal": -40,
    "total": 1300
  }
}`;

export const purchaseOrderExportSampleXml = `<?xml version="1.0" encoding="utf-8"?>
<purchaseOrderExport>
  <schemaVersion>1.0</schemaVersion>
  <documentType>purchaseOrder</documentType>
  <generatedUtc>2026-09-23T15:20:00+00:00</generatedUtc>
  <resourceId>44444444-4444-4444-4444-444444444444</resourceId>
  <documentNumber>PO-2210</documentNumber>
  <currency>USD</currency>
  <purchaseOrder>
    <number>PO-2210</number>
    <customNumber>ACME-55</customNumber>
    <title>Façade phase 2 – Müller residence 住宅</title>
    <issuedDate>2026-09-23</issuedDate>
    <proposedInstallationDate>2026-12-01</proposedInstallationDate>
    <supplier>Northern Frames</supplier>
    <supplierReference>NF-99871</supplierReference>
    <supplierStatus>Accepted</supplierStatus>
    <supplierDeliveryDate>2026-11-20</supplierDeliveryDate>
    <supplierShippingLeadTimeDays>14</supplierShippingLeadTimeDays>
    <assignedTo>
      <name>Kim Lead</name>
    </assignedTo>
    <project>
      <number>P-77</number>
      <title>Riverside</title>
    </project>
    <dealership>
      <name>Acme Windows</name>
    </dealership>
    <deliveryAddress>
      <source>Project</source>
      <line1>14 Riverside Way</line1>
    </deliveryAddress>
  </purchaseOrder>
  <lineItems>
    <lineItem>
      <itemNumber>1</itemNumber>
      <sequence>1</sequence>
      <lineType>frame</lineType>
      <batchId>55555555-5555-5555-5555-555555555555</batchId>
      <batchName>Batch A</batchName>
      <title>Kitchen</title>
      <description>Casement, 2 light</description>
      <optionsSummary>1200 x 900</optionsSummary>
      <quantity>4</quantity>
      <productLine>NF-200</productLine>
      <price>1250</price>
    </lineItem>
    <lineItem>
      <itemNumber>1</itemNumber>
      <sequence>2</sequence>
      <lineType>specialItem</lineType>
      <batchId>66666666-6666-6666-6666-666666666666</batchId>
      <batchName>Batch B</batchName>
      <title>Trim set</title>
      <quantity>1</quantity>
      <price>90</price>
    </lineItem>
  </lineItems>
  <deliveryBatches>
    <deliveryBatch>
      <id>55555555-5555-5555-5555-555555555555</id>
      <name>Batch A</name>
      <detail>Deliver 2026-11-20</detail>
      <subtotal>1250</subtotal>
      <lineSequences>
        <sequence>1</sequence>
      </lineSequences>
    </deliveryBatch>
    <deliveryBatch>
      <id>66666666-6666-6666-6666-666666666666</id>
      <name>Batch B</name>
      <subtotal>90</subtotal>
      <lineSequences>
        <sequence>2</sequence>
      </lineSequences>
    </deliveryBatch>
  </deliveryBatches>
  <supplierPurchaseOrderTerms>Goods remain the property of the supplier until paid.</supplierPurchaseOrderTerms>
  <priceSummary>
    <netTotal>1340</netTotal>
    <adjustmentTotal>-40</adjustmentTotal>
    <total>1300</total>
  </priceSummary>
</purchaseOrderExport>`;
